import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Package, RefreshCw, Plus, Trash2, Pencil, ArrowRight, Warehouse } from "lucide-react";
import { toast } from "sonner";

const fmt = (n, u="") => `${Number(n||0).toLocaleString("en-IN",{maximumFractionDigits:2})}${u?" "+u:""}`;

export default function MaterialsTab({ pid, user }) {
  const [rows, setRows] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ material_name: "", unit: "Nos", required_qty: 0, ordered_qty: 0, received_qty: 0, consumed_qty: 0, rate: 0, supplier: "", inventory_id: "", category: "Material" });
  const [editId, setEditId] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const canEdit = ["super_admin","director","architect","interior_designer","site_engineer","purchase_manager"].includes(user?.role);

  const load = () => api.get(`/projects/${pid}/mrl`).then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/inventory").then(r => setInventory(r.data)).catch(()=>{}); /* eslint-disable-next-line */ }, [pid]);

  const openNew = () => { setForm({ material_name: "", unit: "Nos", required_qty: 0, ordered_qty: 0, received_qty: 0, consumed_qty: 0, rate: 0, supplier: "", inventory_id: "", category: "Material" }); setEditId(null); setOpen(true); };
  const openEdit = (r) => { setForm({ material_name: r.material_name, unit: r.unit, required_qty: r.required_qty, ordered_qty: r.ordered_qty, received_qty: r.received_qty, consumed_qty: r.consumed_qty, rate: r.rate, supplier: r.supplier||"", inventory_id: r.inventory_id||"", category: r.category||"Material" }); setEditId(r.id); setOpen(true); };
  const save = async () => {
    if (!form.material_name.trim()) return toast.error("Material name required");
    const payload = { ...form, inventory_id: form.inventory_id === "__none__" ? "" : form.inventory_id };
    try {
      if (editId) { await api.patch(`/projects/${pid}/mrl/${editId}`, payload); toast.success("Updated"); }
      else { await api.post(`/projects/${pid}/mrl`, payload); toast.success("Added to MRL"); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Remove this material?")) return; await api.delete(`/projects/${pid}/mrl/${id}`); load(); };
  const syncFromBoq = async () => {
    setSyncing(true);
    try { const { data } = await api.post(`/projects/${pid}/mrl/sync-boq`); toast.success(`Sync complete: ${data.added} added, ${data.skipped_or_updated} updated`); load(); }
    catch { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  };
  const pickInventory = (id) => {
    const inv = inventory.find(i => i.id === id);
    if (!inv) return;
    setForm({ ...form, inventory_id: id, material_name: form.material_name || inv.name, unit: inv.unit || form.unit, rate: inv.rate || form.rate, supplier: inv.supplier || form.supplier });
  };

  const totals = rows.reduce((a,r) => ({
    required: a.required + (r.required_qty||0),
    ordered: a.ordered + (r.ordered_qty||0),
    received: a.received + (r.received_qty||0),
    consumed: a.consumed + (r.consumed_qty||0),
    value_required: a.value_required + (r.required_qty||0) * (r.rate||0),
    value_received: a.value_received + (r.received_qty||0) * (r.rate||0),
  }), { required: 0, ordered: 0, received: 0, consumed: 0, value_required: 0, value_received: 0 });

  return (
    <div className="space-y-4" data-testid="mrl-tab">
      <div className="rounded-2xl bg-card border border-border/60 p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-primary" />
          <div>
            <h3 className="font-display text-lg font-semibold">Material Requirement List</h3>
            <p className="text-xs text-muted-foreground">{rows.length} items · auto-sync from BOQ · linked to Inventory</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && <Button variant="outline" onClick={syncFromBoq} disabled={syncing} data-testid="sync-boq-btn"><RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing?"animate-spin":""}`} /> {syncing ? "Syncing…" : "Sync from BOQ"}</Button>}
          {canEdit && <Button onClick={openNew} data-testid="new-mrl-btn" className="bg-primary text-primary-foreground"><Plus className="w-4 h-4 mr-1.5" /> Add Material</Button>}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="Required" value={totals.required} sub={`₹${Math.round(totals.value_required).toLocaleString("en-IN")}`} />
        <KPI label="Ordered" value={totals.ordered} />
        <KPI label="Received" value={totals.received} sub={`₹${Math.round(totals.value_received).toLocaleString("en-IN")}`} />
        <KPI label="Consumed" value={totals.consumed} />
        <KPI label="On Site" value={totals.received - totals.consumed} highlight />
      </div>

      <div className="rounded-2xl bg-card border border-border/60 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
            <tr><th className="text-left px-4 py-3">Material</th><th className="text-right">Required</th><th className="text-right">Ordered</th><th className="text-right">Received</th><th className="text-right">Consumed</th><th className="text-right">Remaining</th><th className="text-right">Rate</th><th className="text-right">Value</th><th></th></tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const remaining = Math.max(0, (r.received_qty||0) - (r.consumed_qty||0));
              const pending_order = Math.max(0, (r.required_qty||0) - (r.ordered_qty||0));
              const value = (r.received_qty||0) * (r.rate||0);
              return (
                <tr key={r.id} data-testid={`mrl-${r.id}`} className="border-t border-border/50 hover:bg-muted/20">
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{r.material_name}</div>
                    <div className="text-[10px] text-muted-foreground">{r.category}{r.supplier?` · ${r.supplier}`:""}{r.boq_item_id?" · from BOQ":""}</div>
                  </td>
                  <td className="text-right">{fmt(r.required_qty, r.unit)}</td>
                  <td className="text-right"><span className={pending_order>0 ? "text-amber-600" : ""}>{fmt(r.ordered_qty)}</span></td>
                  <td className="text-right">{fmt(r.received_qty)}</td>
                  <td className="text-right">{fmt(r.consumed_qty)}</td>
                  <td className="text-right font-medium"><span className={remaining>0 ? "text-emerald-600" : "text-muted-foreground"}>{fmt(remaining)}</span></td>
                  <td className="text-right text-muted-foreground">₹{fmt(r.rate)}</td>
                  <td className="text-right font-medium">₹{Math.round(value).toLocaleString("en-IN")}</td>
                  <td className="pr-4 text-right whitespace-nowrap">
                    {canEdit && <button onClick={() => openEdit(r)} data-testid={`edit-mrl-${r.id}`} className="p-1 text-primary"><Pencil className="w-3.5 h-3.5" /></button>}
                    {canEdit && <button onClick={() => remove(r.id)} data-testid={`delete-mrl-${r.id}`} className="p-1 text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!rows.length && <div className="p-10 text-center text-sm text-muted-foreground"><Warehouse className="w-8 h-8 mx-auto mb-2 opacity-40" /> No materials yet. <button onClick={syncFromBoq} className="text-primary hover:underline">Auto-sync from BOQ</button> or add manually.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editId ? "Edit Material" : "Add Material to MRL"}</DialogTitle><DialogDescription>Link to inventory to auto-fetch rate, unit and supplier.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.inventory_id || "__none__"} onValueChange={v => v==="__none__" ? setForm({...form, inventory_id:""}) : pickInventory(v)}>
              <SelectTrigger className="col-span-2" data-testid="mrl-inventory-select"><SelectValue placeholder="Link to Inventory item (auto-fills)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No link (manual entry) —</SelectItem>
                {inventory.map(i => <SelectItem key={i.id} value={i.id}>{i.name} · {i.unit} · ₹{i.rate}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className="col-span-2" placeholder="Material name" value={form.material_name} onChange={e=>setForm({...form,material_name:e.target.value})} data-testid="mrl-name-input" />
            <Input placeholder="Unit (Nos/Cum/Kg)" value={form.unit} onChange={e=>setForm({...form,unit:e.target.value})} />
            <Input placeholder="Rate ₹" type="number" value={form.rate} onChange={e=>setForm({...form,rate:Number(e.target.value)})} />
            <Input placeholder="Required" type="number" value={form.required_qty} onChange={e=>setForm({...form,required_qty:Number(e.target.value)})} data-testid="mrl-required-input" />
            <Input placeholder="Ordered" type="number" value={form.ordered_qty} onChange={e=>setForm({...form,ordered_qty:Number(e.target.value)})} data-testid="mrl-ordered-input" />
            <Input placeholder="Received" type="number" value={form.received_qty} onChange={e=>setForm({...form,received_qty:Number(e.target.value)})} data-testid="mrl-received-input" />
            <Input placeholder="Consumed" type="number" value={form.consumed_qty} onChange={e=>setForm({...form,consumed_qty:Number(e.target.value)})} data-testid="mrl-consumed-input" />
            <Input className="col-span-2" placeholder="Supplier / Vendor" value={form.supplier} onChange={e=>setForm({...form,supplier:e.target.value})} />
          </div>
          <Button onClick={save} className="bg-primary text-primary-foreground" data-testid="save-mrl-btn">{editId ? "Update" : "Add to MRL"}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KPI({ label, value, sub, highlight }) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "bg-primary/5 border-primary/30" : "bg-card border-border/60"}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
      <div className="font-display text-2xl font-semibold mt-0.5">{Number(value||0).toLocaleString("en-IN",{maximumFractionDigits:2})}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
